import { Fragment, ReactElement, useEffect, useState } from 'react';
import classnames from 'classnames';
import { ResizeObserverHOC } from '../../../hoc/ResizeObserverHOC';
import List, {
	ListItem,
	MDCListItemText,
	MDCListItemMeta,
	MDCListDivider,
} from '../MDC/List/List';
import { IconButton } from '@upmc/ims.component-library';
import Icon from '../MDC/Icon/Icon';
import { CSS_CLASSES } from './_constants';
import { propTypes } from './_types';

import './ListDrawer.scss';

/**
 * Functional component for rendering a list of "drawers". Drawer items respond to user interaction
 * and reveal or conceal contents accordingly.
 *
 * @param {Object} props JSX attributes passed to component.
 * @param {Node} props.drawer JSX drawer contents.
 * @param {string[]|Object[]} props.items array translated into list items.
 * @param {Function} props.onClick callback on drawer interaction.
 * @param {Object} props.props remaining, non-destructured props.
 * @returns {ReactElement} rendered component.
 */
const ListDrawer = ({ drawer, items, onClick, ...props }) => {
	/**
	 * @typedef {Number} sIndexSelected Local state representing nullable index of selected drawer.
	 */
	/**
	 * @typedef {Function} setIndexSelected Setter for index of selected drawer.
	 */
	const [sIndexSelected, setIndexSelected] = useState(null);
	/**
	 * @typedef {boolean} sOpenDrawer Local state indicating whether a drawer is open.
	 * Is necessary because elements must be in the DOM in order to be animated so a delay
	 * is created between drawer state updates and selected index updates, allowing for closing
	 * animations on the drawer of selected index.
	 */
	/**
	 * @typedef {Function} setOpenDrawer Setter for drawer state indication.
	 */
	const [sOpenDrawer, setOpenDrawer] = useState(false);

	/**
	 * Handler for drawer interactions. Conditionally delays updates to {@link sIndexSelected}, conditionally modifies drawer styling.
	 *
	 * When a drawer closes, the element must remain in the DOM sufficiently long enough for its closing animation to be observed.
	 * Consequently, the selected index is reset only after a delay. The delay must not be too long because the list of
	 * drawers will be once more available for user selection, threatening a race condition where the user opens a new drawer
	 * before the old drawer's selected index is reset.
	 *
	 * When opened, a drawer animates up to an absolute "top" position of 0. To make it look like the drawer contents
	 * are originating from the correct drawer, the content's initial "top" position is calculated relative to the drawer's location.
	 *
	 * @param {Number} indexSelected index of selected drawer.
	 * @returns void
	 */
	const onDrawerHandler = indexSelected => {
		const isOpen = indexSelected !== null;
		setOpenDrawer(isOpen);

		onClick(indexSelected);

		const elDrawer = document.getElementById(CSS_CLASSES.DRAWER);
		const elListItems = document.getElementsByClassName(
			CSS_CLASSES.LIST_ITEM
		);
		const listHeight = elListItems?.[0]?.offsetHeight ?? 48;

		// set initial drawer position
		elDrawer.style.top = `${(listHeight * indexSelected + 100) / 16}rem`;

		// delay index reset on drawer close
		window.setTimeout(
			index => {
				setIndexSelected(index);
			},
			isOpen ? 0 : 250,
			indexSelected
		);
	};

	/**
	 * Handler invoked on change to drawer size. Ensures the drawer container respects the absolutely
	 * positioned drawer by dynamically computing the container's height according to the drawer's height.
	 *
	 * @param {ResizeObserverEntry[]} entries observed element dimensions object.
	 * @returns void
	 */
	const onResizeHandler = entries => {
		const borderBoxSize = entries?.[0]?.borderBoxSize;
		const elContainer = document.getElementById(CSS_CLASSES.CONTAINER);

		if (borderBoxSize?.length > 0) {
			// compute container height
			elContainer.style.height = `${borderBoxSize[0].blockSize + 50}px`;
		}
	};

	/**
	 * Hook on change to index selection. Resets the dynamically computed drawer container height on drawer close.
	 */
	useEffect(() => {
		if (sIndexSelected === null) {
			const elContainer = document.getElementById(CSS_CLASSES.CONTAINER);

			elContainer.style.height = '';
		}
	}, [sIndexSelected]);

	return (
		<div className={CSS_CLASSES.CONTAINER} id={CSS_CLASSES.CONTAINER}>
			<List
				className={classnames({
					[CSS_CLASSES.LIST]: true,
					[CSS_CLASSES.LIST_HIDDEN]: sOpenDrawer,
					[CSS_CLASSES.LIST_VISIBLE]: !sOpenDrawer,
				})}
				handleSelect={onDrawerHandler}
				orientation='vertical'
				twoLine={props.twoLine}
				xlarge
			>
				{items.map((item, index) => (
					<Fragment key={item?.Id ?? index}>
						<ListItem
							className={classnames({
								[CSS_CLASSES.LIST_ITEM]: true,
								[CSS_CLASSES.LIST_ITEM_SELECTED]:
									index === sIndexSelected,
							})}
						>
							<MDCListItemText
								primaryText={item?.Title ?? item}
								secondaryText={item?.Subtitle}
							/>
							<MDCListItemMeta
								meta={<Icon icon='expand_more' />}
							/>
						</ListItem>
						{index !== items.length - 1 && <MDCListDivider />}
					</Fragment>
				))}
			</List>
			<div
				id={CSS_CLASSES.DRAWER}
				className={classnames([
					CSS_CLASSES.DRAWER,
					{
						[CSS_CLASSES.DRAWER_OPENED]: sIndexSelected !== null,
						[CSS_CLASSES.DRAWER_CLOSED]: !sOpenDrawer,
					},
				])}
			>
				{sIndexSelected !== null && drawer && (
					<>
						<IconButton
							className={CSS_CLASSES.DRAWER_CLOSE_ICON}
							onClick={() => onDrawerHandler(null)}
						>
							<Icon icon='clear' />
						</IconButton>
						<ResizeObserverHOC callback={onResizeHandler}>
							<div
								className={CSS_CLASSES.DRAWER_CONTENTS}
								id={CSS_CLASSES.DRAWER_CONTENTS}
							>
								{drawer}
							</div>
						</ResizeObserverHOC>
					</>
				)}
			</div>
		</div>
	);
};

ListDrawer.propTypes = propTypes;

export { ListDrawer };
